// Which Explorer a viewer gets.
//
// The Explorer is one set of screens serving two roles, because the alternative
// was two near-identical sets (the old Customer Support pages were a duplicate
// of these, reading a different payload). What differs is the DATA SOURCE and
// which tabs exist, never the layout:
//
//   full    — holds `explorer` (or `talent_management`) at FULL access. Reads the
//             legacy /api/admin/{users,projects} routes: the whole record,
//             including the de-anonymized seller threads.
//   viewer  — holds one of those areas at READ access (migration 0120, and what
//             0119's `viewer_only` holders were migrated to). Reads the SAME
//             routes as full mode: same record, same tabs, same threads. What it
//             does not get is a single control — no Pause agent, no Stop and
//             notify client, no Send as Mira, no shortlist.
//   support — holds `customer_support` only. Reads /api/admin/support/*: the
//             narrow, talent-free payload, so the Sellers tab does not exist.
//   none    — none of those; `<AreaGate>` shows the access-required panel first,
//             so pages never actually render in this mode.
//
// THE MODE IS UX, NOT SECURITY, in both directions. A support-mode viewer is not
// merely shown fewer tabs — they are pointed at a different endpoint, and the
// endpoint they are NOT pointed at would 403 them anyway (`GET
// /api/admin/projects/{id}` is `talent_management` OR `explorer`, at `read`). A
// viewer-mode viewer is not merely shown no buttons — the four operator writes
// take the same two areas at `full` server-side and would 403 them too. The
// server is the boundary; this picks the call that will succeed and declines to
// draw a control that would not.
import { useQuery } from "@tanstack/react-query";

import { type Area, type GrantedAreas, canWrite, hasArea } from "./areas";
import { myAreasQuery } from "./queries";

export type ExplorerMode = "full" | "viewer" | "support" | "none";

/** Modes whose viewer may WRITE. The page-level question is never "which mode is
 *  this" but "may this person act", so ask it here once rather than spelling out
 *  a mode comparison at each control. */
export function canAct(mode: ExplorerMode): boolean {
  return mode === "full";
}

/** Which sections the Explorer shows. One tab per kind of thing, and `people` is
 *  the landing view. Held in the URL as `?view=`.
 *
 *  Two tabs were REMOVED and should not come back without a reason neither had:
 *  - `all` stacked both lists into one scroll. It was the landing view, so the
 *    default screen was the one nobody could find anything on, and it made
 *    "all projects" read as a section you scroll past rather than a place you
 *    go. Landing on `people` costs nothing: the other tab is one click away and
 *    is still its own linkable destination.
 *  - `talents` never had a list behind it. It rendered a "Coming soon" card,
 *    because talent figures are BI-owned and this console must not grow a
 *    second source for them (see the Talent Management rules in CLAUDE.md). A
 *    tab that can only ever say "not here" is worse than no tab. The per-talent
 *    PAGE (`/explorer/talents/$sellerId`) is unaffected and still open from a
 *    project's Sellers tab, which is the route that actually has data. */
export const EXPLORER_VIEWS = ["people", "projects"] as const;
export type ExplorerView = (typeof EXPLORER_VIEWS)[number];

/** Narrow a raw `?view=` value, defaulting to `people`. Tolerant on purpose: a
 *  stale or hand-edited URL should land on the Explorer, not on nothing — which
 *  now includes the retired `?view=all` and `?view=talents` links still sitting
 *  in someone's bookmarks or a Slack message. */
export function parseExplorerView(raw: unknown): ExplorerView {
  return typeof raw === "string" && (EXPLORER_VIEWS as readonly string[]).includes(raw)
    ? (raw as ExplorerView)
    : "people";
}

/** The people list's status filter, held in the URL as `?status=`.
 *
 *  `?status=blocked` is the answer to "show me every account we have turned the
 *  assistant off for", which an operator otherwise has to reconstruct by sorting
 *  the Status column and scrolling. It is a FILTER on the one people list rather
 *  than a page of its own, because the last screen that duplicated this list
 *  (User Administration) was deleted for being a duplicate.
 *
 *  A COMMA LIST since the control above the table became a multi-select, the
 *  same shape `?stage=` uses, so a narrowed view is still a link somebody can be
 *  sent. `?status=blocked` means exactly what it always meant; the widening only
 *  adds `active` and the pair. */
export const PERSON_STATUSES = ["active", "blocked"] as const;
export type PersonStatus = (typeof PERSON_STATUSES)[number];

/** Narrow a raw `?status=` value to the statuses this console knows; unknown
 *  entries drop out and an empty result means no filter. Tolerant for the same
 *  reason `parseExplorerView` is: a hand-edited or stale URL should show the
 *  whole list, never an empty table or a crash. */
export function parsePersonStatuses(raw: unknown): PersonStatus[] {
  if (typeof raw !== "string") return [];
  const asked = new Set(raw.split(","));
  // Filtered off the canonical tuple, so the result is deduped and ordered
  // however the console orders statuses, not however the URL happened to.
  return PERSON_STATUSES.filter((status) => asked.has(status));
}

/** The areas that open the Explorer at all — the gate the routes use. Level-blind:
 *  a read-only grant still opens the page, it just draws no controls. */
export const EXPLORER_AREAS: readonly Area[] = ["explorer", "customer_support"];

/** May this viewer FOLLOW a link into the Explorer? Five other areas deep-link
 *  into it (Alerts, Big clients, Funnel, Searches, Talent Management), and the
 *  areas are granted independently, so "holds Alerts" says nothing about whether
 *  the project behind an alert will open.
 *
 *  Asked against `EXPLORER_AREAS` — the list the routes' `<AreaGate>` actually
 *  checks — and NOT via `explorerModeFor`, which answers a different question and
 *  would answer it wrongly here: it resolves `talent_management` to full mode
 *  because that area reaches the same payload SERVER-side, but the console's
 *  gate does not list it, so a talent-management-only holder is shown the
 *  access-required panel. Deciding a link from the mode would draw exactly the
 *  dead links this exists to remove. */
export function canOpenExplorer(granted: GrantedAreas): boolean {
  return EXPLORER_AREAS.some((area) => hasArea(granted, area));
}

/** `canOpenExplorer` over the same cached my-areas query everything else reads.
 *  The pure function is the RULE and this hook is its only React spelling, so a
 *  component and a test can never answer this question differently.
 *
 *  No `known` companion, deliberately. Every caller renders inside an
 *  `<AreaGate>`, which holds its children behind `<LoadingState>` until this
 *  query resolves, so a link is never drawn against a pending cache. And a link
 *  is not a claim about anyone's access either way: the worst a false negative
 *  does is show a name as text. */
export function useCanOpenExplorer(): boolean {
  const granted = useQuery(myAreasQuery);
  return canOpenExplorer(granted.data ?? []);
}

/** Resolve a granted set to a mode. Pure, so it is unit-testable without React.
 *
 *  Checked WIDEST FIRST, never by grant order, because the areas are additive
 *  and a person can hold several. Holding one area read-only and another in full
 *  is not a restriction — the stronger grant decides, exactly as the server's
 *  union gate does. Anyone who must not act holds no acting area at `full`. */
export function explorerModeFor(granted: GrantedAreas): ExplorerMode {
  // `talent_management` already reaches the fat payload server-side, so someone
  // holding it should not be handed the narrower one.
  if (canWrite(granted, "explorer") || canWrite(granted, "talent_management")) return "full";
  // Same payload as full mode, so it outranks support mode: handing a viewer the
  // talent-free read would hide a record they are entitled to.
  if (hasArea(granted, "explorer") || hasArea(granted, "talent_management")) return "viewer";
  if (hasArea(granted, "customer_support")) return "support";
  return "none";
}

/** The signed-in employee's Explorer mode. Reads the same cached my-areas query
 *  the sidebar and `<AreaGate>` use — no extra request, no second source of
 *  truth. Returns "none" while the query is still loading; pages render their
 *  loading state off the DATA query, not off this. */
export function useExplorerMode(): ExplorerMode {
  const areas = useQuery(myAreasQuery);
  return explorerModeFor(areas.data ?? []);
}
